import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { MessageSquare, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import { API_URL } from '@/services/apiClient';

interface MessageSafetyData {
  totalMessages: number;
  totalFlagged: number;
  pendingReview: number;
  reviewed: number;
  byPlatform: Array<{ platform: string; total: number; flagged: number }>;
}

const PLATFORM_COLORS = [
  'bg-blue-500', 'bg-purple-500', 'bg-emerald-500',
  'bg-orange-500', 'bg-pink-500', 'bg-amber-500',
];

export function MessageSafetyReport({ dm }: { dm: boolean }) {
  const [data, setData] = useState<MessageSafetyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const cardCls = `border shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
  const titleCls = dm ? 'text-white' : 'text-slate-800';
  const descCls = dm ? 'text-white' : 'text-slate-500';
  const bodyTextCls = dm ? 'text-white' : 'text-slate-700';
  const mutedCls = dm ? 'text-white' : 'text-slate-400';

  const fetch_ = useCallback(async () => {
    setLoading(true);
    const token = sessionStorage.getItem('authToken');
    try {
      const res = await fetch(`${API_URL}/dashboard/school-admin/message-safety?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch {/* */} finally { setLoading(false); }
  }, [days]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const flagPct = data && data.totalMessages > 0 ? Math.round((data.totalFlagged / data.totalMessages) * 100) : 0;
  const reviewPct = data && data.totalFlagged > 0 ? Math.round((data.reviewed / data.totalFlagged) * 100) : 0;

  return (
    <div className="space-y-5" data-dm={dm}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className={`text-lg font-semibold ${titleCls}`}>Message Safety Report</h2>
        <div className="flex gap-2">
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)}
              className={`btn-glass btn-glass-pill text-sm ${days === d ? 'btn-glass-blue' : ''}`}>{d}d</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'Total Messages', value: data?.totalMessages ?? 0, icon: MessageSquare, color: 'blue', sub: `Last ${days} days` },
              { label: 'Flagged', value: data?.totalFlagged ?? 0, icon: AlertTriangle, color: 'red', sub: `${flagPct}% flag rate` },
              { label: 'Pending Review', value: data?.pendingReview ?? 0, icon: Clock, color: 'amber', sub: 'Needs attention' },
              { label: 'Reviewed', value: data?.reviewed ?? 0, icon: CheckCircle2, color: 'emerald', sub: `${reviewPct}% reviewed` },
            ].map((s, i) => (
              <Card key={i} className={cardCls}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-2.5">
                    <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${dm ? `bg-${s.color}-900/50` : `bg-${s.color}-100`}`}>
                      <s.icon className={`h-4 w-4 text-${s.color}-500`} />
                    </div>
                    <div>
                      <p className={`text-xs ${descCls}`}>{s.label}</p>
                      <p className={`text-xl font-bold ${s.color === 'red' && s.value > 0 ? 'text-red-500' : s.color === 'amber' && s.value > 0 ? 'text-amber-500' : titleCls}`}>{s.value.toLocaleString()}</p>
                      <p className={`text-xs ${mutedCls}`}>{s.sub}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Platform breakdown */}
          <Card className={cardCls}>
            <CardHeader className="pb-3">
              <CardTitle className={`text-base ${titleCls}`}>Message Safety by Platform</CardTitle>
              <CardDescription className={descCls}>Total vs flagged messages per communication channel</CardDescription>
            </CardHeader>
            <CardContent>
              {data?.byPlatform.length ? (
                <div className="space-y-4">
                  {data.byPlatform.sort((a, b) => b.flagged - a.flagged).map((p, i) => {
                    const flagRate = p.total > 0 ? Math.round((p.flagged / p.total) * 100) : 0;
                    const colorClass = PLATFORM_COLORS[i % PLATFORM_COLORS.length];
                    return (
                      <div key={i} className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`h-2.5 w-2.5 rounded-full ${colorClass}`} />
                            <span className={`text-sm font-medium capitalize ${bodyTextCls}`}>{p.platform}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`text-xs ${mutedCls}`}>{p.total} total</span>
                            <span className={`text-xs font-semibold ${p.flagged > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                              {p.flagged} flagged ({flagRate}%)
                            </span>
                          </div>
                        </div>
                        <div className={`h-2 rounded-full overflow-hidden ${dm ? 'bg-slate-700' : 'bg-slate-100'}`}>
                          <div className="h-full flex">
                            <div className={`h-full ${colorClass} transition-all duration-500`}
                              style={{ width: `${p.total > 0 ? Math.round(((p.total - p.flagged) / p.total) * 100) : 0}%` }} />
                            <div className="h-full bg-red-500 transition-all duration-500"
                              style={{ width: `${flagRate}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div className="flex items-center gap-4 pt-1">
                    <div className="flex items-center gap-1.5">
                      <div className={`h-2.5 w-2.5 rounded-sm ${dm ? 'bg-blue-600' : 'bg-blue-500'}`} />
                      <span className={`text-xs ${mutedCls}`}>Safe messages</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="h-2.5 w-2.5 rounded-sm bg-red-500" />
                      <span className={`text-xs ${mutedCls}`}>Flagged messages</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={`flex flex-col items-center justify-center h-32 ${mutedCls}`}>
                  <MessageSquare className="h-8 w-8 mb-2 opacity-40" />
                  <p className="text-sm">No message data for this period</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
